import { Node, Edge } from 'reactflow';
import { QAPair, Conflict, NodeData } from '../types';
import { hasConflicts, getConflictsForNode } from '../analyzers/conflictDetector';
import {
  identifySiblingGroups,
  SiblingGroup,
} from './siblingGrouping';

/**
 * Configuration for graph building
 */
export interface GraphConfig {
  enableSiblingGrouping?: boolean;
  showGhostNodes?: boolean;
  allQAPairs?: QAPair[]; // Full list for dependency matching
}

/**
 * Convert QAPairs into ReactFlow nodes and edges
 *
 * @param qaPairs - The filtered QA pairs to display as nodes
 * @param conflicts - Conflicts to highlight
 * @param config - Configuration options
 * @returns Graph with nodes, edges, and optional sibling groups
 */
export function buildGraph(
  qaPairs: QAPair[],
  conflicts: Conflict[],
  config: GraphConfig = {}
): { nodes: Node<NodeData>[]; edges: Edge[]; siblingGroups?: SiblingGroup[] } {
  let nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];
  let siblingGroups: SiblingGroup[] | undefined;

  const { enableSiblingGrouping = true, showGhostNodes = false, allQAPairs = qaPairs } = config;

  // Create a map for quick lookup of VISIBLE nodes (filtered)
  const visibleNodeMap = new Map<string, QAPair>();
  for (const pair of qaPairs) {
    visibleNodeMap.set(pair.question.id, pair);
  }

  // Create a map for ALL nodes (for dependency matching)
  const allNodeMap = new Map<string, QAPair>();
  for (const pair of allQAPairs) {
    allNodeMap.set(pair.question.id, pair);
  }

  // Create nodes
  for (const pair of qaPairs) {
    const nodeConflicts = getConflictsForNode(pair.question.id, conflicts);
    const nodeHasConflict = hasConflicts(pair.question.id, conflicts);

    const node: Node<NodeData> = {
      id: pair.question.id,
      type: 'qaNode', // Custom node type
      position: { x: 0, y: 0 }, // Will be set by layout engine
      data: {
        qaPair: pair,
        hasConflict: nodeHasConflict,
        conflicts: nodeConflicts,
      },
    };

    nodes.push(node);
  }

  // Identify sibling groups if enabled (backgrounds will be added after layout)
  if (enableSiblingGrouping) {
    siblingGroups = identifySiblingGroups(qaPairs);
  }

  // Create parent-child edges (hierarchical structure)
  for (const pair of qaPairs) {
    if (pair.parentId) {
      edges.push({
        id: `e-${pair.parentId}-${pair.question.id}`,
        source: pair.parentId,
        target: pair.question.id,
        type: 'default',
        style: { stroke: '#64748b', strokeWidth: 2 },
        animated: false,
      });
    }
  }

  // Create dependency edges (cross-references)
  // Track ghost nodes we need to create - store both ghost ID and original target ID
  const ghostNodes = new Map<string, string>(); // ghostId -> originalTargetId

  for (const pair of qaPairs) {
    for (const dep of pair.answer.dependencies) {
      // Try to find target node with multiple strategies using ALL nodes
      let targetId = dep.targetId;
      let foundTarget = false;
      let targetPair: QAPair | undefined;

      // Strategy 1: Exact match
      if (allNodeMap.has(targetId)) {
        foundTarget = true;
        targetPair = allNodeMap.get(targetId);
      }
      // Strategy 2: Try adding "Root." prefix (for references like "How.1" -> "Root.How.1")
      else if (!foundTarget) {
        const rootTargetId = `Root.${targetId}`;
        if (allNodeMap.has(rootTargetId)) {
          targetId = rootTargetId;
          foundTarget = true;
          targetPair = allNodeMap.get(targetId);
        }
      }
      // Strategy 3: Search for any node ending with the target pattern
      // e.g., "How.1" could match "Who.1.How.1" or "Root.How.1"
      if (!foundTarget) {
        for (const nodeId of allNodeMap.keys()) {
          if (nodeId.endsWith(`.${targetId}`) || nodeId === targetId) {
            targetId = nodeId;
            foundTarget = true;
            targetPair = allNodeMap.get(nodeId);
            break;
          }
        }
      }

      // Create edge if target was found in the full list
      if (foundTarget && targetPair) {
        // If target is not visible but ghost nodes are enabled, create a ghost node
        let edgeTargetId = targetId;
        if (!visibleNodeMap.has(targetId) && showGhostNodes) {
          // Use a ghost- prefix to avoid ID conflicts with real nodes
          edgeTargetId = `ghost-${targetId}`;
          ghostNodes.set(edgeTargetId, targetId);
        }

        // Create dependency edge (will work whether target is visible or ghost)
        edges.push({
          id: `d-${pair.question.id}-${edgeTargetId}`,
          source: pair.question.id,
          target: edgeTargetId,
          type: 'default',
          style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
          animated: true,
          label: dep.description,
          labelStyle: { fontSize: 10, fill: '#dc2626', fontWeight: 'bold' },
        });
      } else if (showGhostNodes) {
        // Dependency target doesn't exist anywhere - create a ghost node for it
        const ghostId = `ghost-missing-${dep.targetId}`;
        ghostNodes.set(ghostId, dep.targetId);

        edges.push({
          id: `d-${pair.question.id}-${ghostId}`,
          source: pair.question.id,
          target: ghostId,
          type: 'default',
          style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
          animated: true,
          label: dep.description,
          labelStyle: { fontSize: 10, fill: '#dc2626', fontWeight: 'bold' },
        });
      } else {
        // Log missing dependency for debugging (only if not showing ghost nodes)
        console.warn(`Dependency target not found: "${dep.targetId}" from node ${pair.question.id}`);
      }
    }
  }

  // Create ghost nodes if enabled
  if (showGhostNodes && ghostNodes.size > 0) {
    for (const [ghostId, originalTargetId] of ghostNodes.entries()) {
      // Check if this is a real node that's just filtered out, or a truly missing dependency
      const realNode = allNodeMap.get(originalTargetId);

      if (realNode) {
        // Filtered-out node - create a dimmed version with ghost- prefix ID
        const node: Node<NodeData> = {
          id: ghostId, // Use the ghost ID to avoid conflicts
          type: 'qaNode',
          position: { x: 0, y: 0 },
          data: {
            qaPair: realNode,
            hasConflict: false,
            conflicts: [],
            isGhost: true,
            ghostReason: 'filtered',
          },
          style: {
            opacity: 0.4,
            border: '2px dashed #9ca3af',
          },
        };
        nodes.push(node);
      } else {
        // Truly missing dependency - create a placeholder ghost node
        const ghostQAPair: QAPair = {
          question: {
            id: ghostId,
            questionText: `Missing: ${originalTargetId}`,
            breadcrumb: 'Unresolved Dependency',
            depth: 'L1',
          },
          answer: {
            id: ghostId,
            answerText: 'This dependency reference points to a node that does not exist in the plan.',
            dependencies: [],
            branchStatus: 'Explore deeper',
          },
          parentId: undefined,
        };

        const node: Node<NodeData> = {
          id: ghostId,
          type: 'qaNode',
          position: { x: 0, y: 0 },
          data: {
            qaPair: ghostQAPair,
            hasConflict: false,
            conflicts: [],
            isGhost: true,
            ghostReason: 'missing',
          },
          style: {
            opacity: 0.3,
            border: '2px dashed #ef4444',
            background: '#fef2f2',
          },
        };
        nodes.push(node);
      }
    }
  }

  return { nodes, edges, siblingGroups };
}

/**
 * Get color based on depth level
 */
export function getNodeColor(depth: string): string {
  switch (depth) {
    case 'L1':
      return '#3b82f6'; // Blue
    case 'L2':
      return '#10b981'; // Green
    case 'L3':
      return '#8b5cf6'; // Purple
    case 'L4':
      return '#f59e0b'; // Amber
    case 'L5':
      return '#ec4899'; // Pink
    default:
      return '#6b7280'; // Gray
  }
}

/**
 * Get node style based on its properties
 */
export function getNodeStyle(hasConflict: boolean, depth: string) {
  const baseColor = getNodeColor(depth);

  return {
    background: hasConflict ? '#fee2e2' : '#ffffff',
    border: hasConflict ? '3px solid #ef4444' : `2px solid ${baseColor}`,
    borderRadius: '8px',
    padding: '10px',
    minWidth: '200px',
    maxWidth: '350px',
  };
}
